"use client";
import React, { useState, useEffect } from "react";
import { usefetchDataFromApi } from "@/utils/api";
import handleApiError from "@/utils/handleApiError";
import { useSelector, useDispatch } from "react-redux";
import { getUserDetails, getUserLogout } from "@/redux/user/userSlice";
import { resetAnonymousId } from "@/utils/anonymousId";
import { getApiToken } from "@/redux/config/configSlice";
import Link from "next/link";
import { useRouter } from "next/navigation";

const PersonalInfo = () => {
  const { userDetails } = useSelector((state) => state.user);
  // userDetails comes from redux-persist (client-only) and is absent during SSR.
  // Render it only after mount so the server HTML and first client render match,
  // otherwise React throws a hydration mismatch on the name/email/mobile.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const user = mounted ? userDetails : null;
  const [show, setShow] = useState(0);
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [email, setEmail] = useState("");
  const [saveError, setSaveError] = useState("");

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const dispatch = useDispatch();
  const router = useRouter();

  const piFormCard = "bg-white rounded-card-lg border border-black/[0.06] shadow-card-sm p-6 max-md:p-4 mb-4 transition-shadow duration-[250ms] hover:shadow-card";
  const piEditBtn = "text-textMuted font-medium text-sm px-4 py-1.5 rounded-card transition-all duration-200 whitespace-nowrap shrink-0 hover:bg-redSurface-50 hover:text-heading";
  const piInput = "border border-black/[0.12] rounded-xl py-3 px-4 h-12 w-full text-sm font-medium transition-[border-color,box-shadow] duration-200 focus:border-heading focus:shadow-[0_0_0_3px_rgba(0,0,0,0.06)] outline-none";
  const piInputLabel = "absolute left-4 -top-2 bg-white px-1.5 text-xs text-textMuted font-medium";
  const piSaveBtn = "inline-flex items-center justify-center h-12 px-7 rounded-xl bg-heading text-white font-semibold text-sm transition-all duration-200 hover:bg-headingHover";
  const piModalOverlay = "fixed inset-0 z-[55] bg-black/45 backdrop-blur-[4px] flex items-center justify-center";
  const piModalCard = "bg-white rounded-3xl shadow-modal p-7 max-md:p-5 w-[90%] max-w-[520px]";
  const piDeleteBtn = "inline-flex items-center justify-center h-11 px-5 rounded-xl bg-danger text-white font-semibold text-sm transition-all duration-200 hover:bg-dangerDeep disabled:opacity-60";

  // Close modal on Escape key
  useEffect(() => {
    if (show !== 4 && show !== 5) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !deleteLoading) setShow(0);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, deleteLoading]);


  const handleShowDetails = () => {
    usefetchDataFromApi({
      url: "user/profile",
      method: "POST",

      data: {
        id: userDetails?.id,
        platform: 3,
      },
    })
      .then((res) => {
        if (res.success) {
          dispatch(
            getUserDetails({ userDetails: res.data?.userData, isAuthenticated: true })
          );
        }
      })
      .catch(() => {
      });
  };

  const handeUserDetailsEdit = (id) => {
    setSaveError("");
    let editData = {};
    if (id == 1) {
      editData = { id: userDetails?.id, platform: 3, fname, lname };
    } else if (id == 2) {
      editData = { id: userDetails?.id, platform: 3, email };
    }

    usefetchDataFromApi({
      url: "user/profileupdate",
      method: "POST",
      data: editData,
    })
      .then(() => {
        setShow(0);
        handleShowDetails();
      })
      .catch((err) => {
        setSaveError(handleApiError(err));
      });
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");
    setDeleteLoading(true);
    try {
      const response = await usefetchDataFromApi({
        url: "user/delete-account",
        method: "POST",
        data: {
          platform: 3,
        },
      });

      if (response.success) {
        dispatch(getUserLogout());
        resetAnonymousId();
        dispatch(getApiToken(""));
        setShow(0);
        router.push("/");
      } else {
        setDeleteError(
          response.message || "Unable to delete account"
        );
      }
    } catch (e) {
      setDeleteError("Unable to delete account");
    } finally {
      setDeleteLoading(false);
    }
  };


  return (
    <main className="w-full bg-bgGray50 min-h-screen">
        <div className="mx-[5%] lg:mx-[216px]">
          <div className="profile relative">
            <div className="journey-topbar lg:hidden fixed top-[14px] left-0 right-0 z-20 bg-white/85 backdrop-blur-[12px] py-[14px] px-5 flex items-center gap-2.5">
              <Link href="/user/dashboard" className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-[background] duration-200 hover:bg-black/5">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M15 19l-7-7 7-7" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <h2 className="text-[15px] font-semibold tracking-[-0.2px] text-heading">Personal Information</h2>
            </div>
            <div className="journey-head hidden lg:flex items-start mt-20 mb-6">
              <Link href="/user/dashboard" className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-[background] duration-200 hover:bg-black/5 mt-0.5 mr-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M15 19l-7-7 7-7" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <div>
                <h1 className="text-lg font-semibold tracking-[-0.2px] text-heading">Personal Information</h1>
                <p className="text-[13px] text-textMuted flex items-center gap-1.5 mt-0.5">
                  <Link href="/user/dashboard" className="hover:text-heading transition-colors">Account</Link>
                  <span className="text-textDisabled">/</span>
                  <span className="text-heading">Personal Information</span>
                </p>
              </div>
            </div>

            <div className="journey-body mt-20 lg:mt-0">
              {saveError && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-[13px] text-red-600 font-medium">
                  {saveError}
                </div>
              )}

              {/* Name — view */}
              <div className={`${piFormCard} flex items-start ${show == 1 ? "hidden" : ""}`}>
                <div className="flex flex-1 gap-8">
                  <div>
                    <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading mb-1">First Name</h3>
                    <p className="text-[13px] text-textMuted">{user?.name || "—"}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading mb-1">Last Name</h3>
                    <p className="text-[13px] text-textMuted">{user?.lastName || "—"}</p>
                  </div>
                </div>
                <button
                  className={piEditBtn}
                  onClick={() => { setSaveError(""); setFname(userDetails?.name || ""); setLname(userDetails?.lastName || ""); setShow(1); }}
                >
                  Edit
                </button>
              </div>

              {/* Name — edit */}
              <div className={`${piFormCard} ${show == 1 ? "" : "hidden"}`}>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading">Name</h3>
                  <button className={piEditBtn} onClick={() => { setSaveError(""); setShow(0); }}>Cancel</button>
                </div>
                <p className="text-[13px] text-textMuted mb-6">
                  This is the name on your travel document, which could be a license or passport.
                </p>
                <div className="md:flex gap-4 items-end">
                  <div className="relative md:w-[38%] mb-4 md:mb-0">
                    <input
                      type="text"
                      onChange={(e) => setFname(e.target.value)}
                      defaultValue={fname}
                      placeholder="Enter your first name"
                      className={piInput}
                    />
                    <label className={piInputLabel}>First Name</label>
                  </div>
                  <div className="relative md:w-[38%] mb-4 md:mb-0">
                    <input
                      type="text"
                      onChange={(e) => setLname(e.target.value)}
                      defaultValue={lname}
                      placeholder="Enter your last name"
                      className={piInput}
                    />
                    <label className={piInputLabel}>Last Name</label>
                  </div>
                  <button onClick={() => handeUserDetailsEdit(1)} className={piSaveBtn}>Save</button>
                </div>
              </div>

              {/* Email — view */}
              <div className={`${piFormCard} flex items-start ${show == 2 ? "hidden" : ""}`}>
                <div className="flex-1">
                  <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading mb-1">Email Address</h3>
                  <p className="text-[13px] text-textMuted">{user?.email || "—"}</p>
                </div>
                <button
                  className={piEditBtn}
                  onClick={() => { setSaveError(""); setEmail(userDetails?.email || ""); setShow(2); }}
                >
                  Edit
                </button>
              </div>

              {/* Email — edit */}
              <div className={`${piFormCard} ${show == 2 ? "" : "hidden"}`}>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading">Email Address</h3>
                  <button className={piEditBtn} onClick={() => { setSaveError(""); setShow(0); }}>Cancel</button>
                </div>
                <p className="text-[13px] text-textMuted mb-6">
                  Use an address you&apos;ll always have access to.
                </p>
                <div className="md:flex gap-4 items-end">
                  <div className="relative md:w-[40%] mb-4 md:mb-0">
                    <input
                      type="email"
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Type here"
                      defaultValue={email}
                      className={piInput}
                    />
                    <label className={piInputLabel}>Email Address</label>
                  </div>
                  <button onClick={() => handeUserDetailsEdit(2)} className={piSaveBtn}>Save</button>
                </div>
              </div>

              {/* Phone — view only */}
              <div className={piFormCard}>
                <div>
                  <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading mb-1">Phone Number</h3>
                  <p className="text-[13px] text-textMuted">{user?.mobile || "—"} <span className="text-textPlaceholder">(Primary)</span></p>
                </div>
              </div>

              <div className={`${piFormCard} flex justify-between items-start`}>
                <div>
                  <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading mb-1">Driving License</h3>
                  <p className="text-[13px] text-textMuted">Provided</p>
                </div>
                <button className={piEditBtn} onClick={() => setShow(4)}>Remove</button>
              </div>

              {show == 4 && (
                <div className={piModalOverlay} onClick={(e) => { if (e.target === e.currentTarget) setShow(0); }}>
                  <div className={piModalCard} role="dialog" aria-modal="true" aria-labelledby="remove-id-title">
                    <div className="flex justify-between items-start mb-4">
                      <h2 id="remove-id-title" className="font-semibold text-lg tracking-[-0.2px] text-heading">
                        What to Know about removing your ID
                      </h2>
                      <button className="text-textMuted hover:text-heading transition-colors p-1" onClick={() => setShow(0)} aria-label="Close dialog">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <p className="text-[14px] text-textMuted leading-[1.6] mb-3">
                      Your identity was verified using ID. If you remove it,
                      you&#39;ll need to get verified again the next time you book -
                      or to continue renting.
                    </p>
                    <p className="text-[14px] text-textMuted leading-[1.6] mb-6">
                      If you have questions about the privacy and security of
                      your info, read our{" "}
                      <Link href="/legal/privacy-policy" className="text-heading font-medium underline underline-offset-2 hover:text-headingHover transition-colors">
                        Privacy Policy
                      </Link>
                    </p>
                    <div className="border-t border-[rgba(0,0,0,0.06)] pt-5 flex justify-between items-center">
                      <button className={piEditBtn} onClick={() => setShow(0)}>Never Mind</button>
                      <button className={piSaveBtn}>Yes, remove ID</button>
                    </div>
                  </div>
                </div>
              )}

              <div className={`${piFormCard} flex justify-between items-start`} style={{ borderColor: "rgba(220, 38, 38, 0.15)" }}>
                <div>
                  <h3 className="font-medium text-[15px] tracking-[-0.2px] text-heading mb-1">Delete Account</h3>
                  <p className="text-[13px] text-textMuted">Permanently delete your account. This action can&#39;t be undone.</p>
                </div>
                <button
                  className={piDeleteBtn}
                  onClick={() => { setDeleteError(""); setShow(5); }}
                >
                  Delete
                </button>
              </div>

              {show == 5 && (
                <div className={piModalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !deleteLoading) setShow(0); }}>
                  <div className={piModalCard} role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
                    <div className="flex justify-between items-start mb-4">
                      <h2 id="delete-account-title" className="font-semibold text-lg tracking-[-0.2px] text-heading">
                        Confirm account deletion
                      </h2>
                      <button
                        className="text-textMuted hover:text-heading transition-colors p-1"
                        onClick={() => { if (!deleteLoading) setShow(0); }}
                        aria-label="Close dialog"
                        disabled={deleteLoading}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <p className="text-[14px] text-textMuted leading-[1.6] mb-3">
                      This will delete your account and remove your personal details from our system.
                      You won&#39;t be able to log in again with this account.
                    </p>
                    {deleteError ? (
                      <p className="text-red-600 text-[13px] font-medium mb-3">{deleteError}</p>
                    ) : null}
                    <div className="border-t border-[rgba(0,0,0,0.06)] pt-5 flex justify-between items-center">
                      <button
                        className={piEditBtn}
                        onClick={() => { if (!deleteLoading) setShow(0); }}
                        disabled={deleteLoading}
                      >
                        Cancel
                      </button>
                      <button
                        className={piDeleteBtn}
                        onClick={handleDeleteAccount}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? "Deleting..." : "Yes, delete my account"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </main>
  );
};

export default PersonalInfo;
